import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { THREE_SECONDS } from '@/constants/time';
import { useAppResume } from '@/hooks/useAppResume';
import { askKeys } from '@/services/ask/queryOptions';

/** Resume signals closer together than this are one resume. */
export const ASK_REFRESH_WINDOW = THREE_SECONDS;

/**
 * Re-reads every Ask query after the app was away, however it came back.
 *
 * The realtime channel keeps no history, so returning to the foreground, the
 * network coming back, and the socket resubscribing all mean the same thing:
 * whatever changed meanwhile was never announced. They also tend to arrive
 * together, a foreground followed moments later by the socket's resume, so
 * they share one refresh and a burst of them costs one round of requests.
 *
 * Returns the refresh for the socket's resume to call; the app's own resume is
 * wired here, through the shared `useAppResume`.
 */
export function useAskRefresh(): () => void {
  const queryClient = useQueryClient();

  const lastRefreshAt = useRef(0);

  const refresh = useCallback(() => {
    const now = Date.now();

    if (now - lastRefreshAt.current < ASK_REFRESH_WINDOW) {
      return;
    }

    lastRefreshAt.current = now;

    queryClient.invalidateQueries({ queryKey: askKeys.all }).catch(() => {});
  }, [queryClient]);

  useAppResume(refresh);

  return refresh;
}
